import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { InquiryPublicDto } from '@paladin-monorepo/common';
import {
  buildMonthlyPipeline,
  countByStatus,
  getActiveProjectsForTable,
} from '@/components/dashboard/inquirer/inquirer-dashboard-data';
import { InquiryPipelineChart } from '@/components/dashboard/inquirer/InquiryPipelineChart';
import { InquiryStatusDonut } from '@/components/dashboard/inquirer/InquiryStatusDonut';
import { InquirerActiveProjectsTable } from '@/components/dashboard/inquirer/InquirerActiveProjectsTable';

type InquirerDashboardSectionProps = {
  inquiries: InquiryPublicDto[];
};

export const InquirerDashboardSection: FC<InquirerDashboardSectionProps> = ({ inquiries }) => {
  const { i18n } = useTranslation();

  const pipelineChartData = useMemo(() => {
    const rows = buildMonthlyPipeline(inquiries);
    const loc = i18n.language;
    return rows.map((r) => ({
      monthLabel: new Intl.DateTimeFormat(loc, { month: 'short' }).format(
        new Date(`${r.monthKey}-01T12:00:00`),
      ),
      cumulativeInquiries: r.cumulativeInquiries,
      cumulativeBudget: r.cumulativeBudget,
    }));
  }, [inquiries, i18n.language]);

  const statusSegments = useMemo(() => countByStatus(inquiries), [inquiries]);
  const activeProjectRows = useMemo(() => getActiveProjectsForTable(inquiries), [inquiries]);

  return (
    <div className="space-y-5">
      <InquirerActiveProjectsTable inquiries={activeProjectRows} />

      <div className="hidden gap-4 md:grid lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <InquiryPipelineChart data={pipelineChartData} />
        </div>
        <div className="min-w-0 lg:col-span-1">
          <InquiryStatusDonut segments={statusSegments} />
        </div>
      </div>
    </div>
  );
};
